'use client'

import * as React from 'react'

type Theme = 'light' | 'dark' | 'system'

interface ThemeContextValue {
  theme: Theme
  resolvedTheme: 'light' | 'dark' | undefined
  setTheme: (theme: Theme) => void
}

const ThemeContext = React.createContext<ThemeContextValue>({
  theme: 'system',
  resolvedTheme: undefined,
  setTheme: () => {},
})

function systemTheme(): 'light' | 'dark' {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

// O script anti-FOUC vive no <head> do layout raiz (next/script, beforeInteractive).
// Este provedor não renderiza nenhuma tag <script>, evitando erros de hidratação.
export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // Primeiro render idêntico ao do servidor; o valor real é lido após a montagem.
  const [theme, setThemeState] = React.useState<Theme>('system')
  const [resolvedTheme, setResolvedTheme] = React.useState<'light' | 'dark' | undefined>(undefined)

  const apply = React.useCallback((next: Theme) => {
    const resolved = next === 'system' ? systemTheme() : next
    document.documentElement.classList.toggle('dark', resolved === 'dark')
    setResolvedTheme(resolved)
  }, [])

  React.useEffect(() => {
    let stored: Theme = 'system'
    try {
      const value = localStorage.getItem('theme')
      if (value === 'light' || value === 'dark') stored = value
    } catch {}
    setThemeState(stored)
    apply(stored)

    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => {
      try {
        if (!localStorage.getItem('theme')) apply('system')
      } catch {
        apply('system')
      }
    }
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [apply])

  const setTheme = React.useCallback(
    (next: Theme) => {
      setThemeState(next)
      try {
        if (next === 'system') localStorage.removeItem('theme')
        else localStorage.setItem('theme', next)
      } catch {}
      apply(next)
    },
    [apply],
  )

  const value = React.useMemo(() => ({ theme, resolvedTheme, setTheme }), [theme, resolvedTheme, setTheme])

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme() {
  return React.useContext(ThemeContext)
}
